import type { ChatKitOptions } from '@openai/chatkit'

export type ChatKitOptionsOverrides = DeepPartial<ChatKitOptions>

export const CHATKIT_BASE_OPTIONS = {
  // Custom backend integration with domain key verification
  // Domain key is verified by ChatKit frontend library with OpenAI servers
  api: {
    url: '/api/chatkit',
    domainKey: import.meta.env.VITE_OPENAI_DOMAIN_KEY,
  },
  theme: {
    colorScheme: 'dark',
    radius: 'soft',
    density: 'normal',
    color: {
      grayscale: {
        hue: 0,
        tint: 2,
      },
      accent: {
        primary: '#a10c81',
        level: 1,
      },
    },
    typography: {
      baseSize: 16,
      fontFamily:
        '"OpenAI Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
      fontFamilyMono:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "DejaVu Sans Mono", "Courier New", monospace',
      fontSources: [
        {
          family: 'OpenAI Sans',
          src: 'https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-Regular.woff2',
          weight: 400,
          style: 'normal',
          display: 'swap',
        },
      ],
    },
  },
  composer: {
    placeholder: 'Ask me anything about wine, juice, beer, distillates and cider production...',
    attachments: {
      enabled: true,
      maxCount: 5,
      maxSize: 10_485_760, // 10MB
    },
  },
  startScreen: {
    greeting: 'Enologic-Ai. Technological brain, powered by Enogrup',
    prompts: [
      {
        icon: 'circle-question',
        label: 'Chardonnay - yeast and nutrient selection for fruity style',
        prompt: 'I have 600 kg of Chardonnay grapes. Please recommend 1–2 yeast strains with a nutrition plan. I want to achieve pronounced fruity notes.',
      },
      {
        icon: 'circle-question',
        label: 'Створюємо пиво в стилі American IPA на 1000 літрів',
        prompt: 'Створи рецепт пива American IPA на 1000 літрів: засип, паузи затирання, цільові IBU/ABV, дріжджі.',
      },
      {
        icon: 'circle-question',
        label: 'Выдержка и обработка дистиллята в дубе',
        prompt: 'Мне нужно смягчить дистиллят крепостью 72% дубом. Желаемый профиль: ваниль/карамель. Предложи варианты (форма/обжарка), дозы на 100 литров и срок выдержки; добавь цены и ссылки на рекомендуемые дубовые продукты.',
      },
      {
        icon: 'circle-question',
        label: 'Filtrarea cidrului',
        prompt: 'Am nevoie de o soluție pentru filtrarea cidrului cu ajutorul unui filtru din carton - aproximativ 500 de litri pe zi.',
      },
    ],
  },
} satisfies ChatKitOptions

/**
 * Compose final ChatKit options by cloning the base configuration and
 * merging any number of override objects. Later overrides win.
 *
 * Usage:
 *   const options = composeChatKitOptions({
 *     api: { url: '/api/chatkit' }
 *   })
 *
 * To test new configs from ChatKit Studio, assign them in the console:
 *   window.__CHATKIT_OPTIONS__ = { theme: { colorScheme: 'light' } }
 */
export function composeChatKitOptions(
  ...overrides: Array<ChatKitOptionsOverrides | undefined>
): ChatKitOptions {
  const result = deepClone(CHATKIT_BASE_OPTIONS)
  overrides.forEach((override) => {
    if (override) {
      deepMergeInPlace(result, override)
    }
  })
  return result
}

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
  ? DeepPartial<U>[]
  : T[P] extends object
  ? DeepPartial<T[P]>
  : T[P]
}

function deepMergeInPlace(target: unknown, source: unknown): void {
  if (!isPlainObject(target) || !isPlainObject(source)) {
    return
  }

  Object.entries(source).forEach(([key, value]) => {
    if (value === undefined) {
      return
    }

    const current = target[key]
    target[key] = mergeValue(current, value)
  })
}

function mergeValue(targetValue: unknown, incomingValue: unknown): unknown {
  if (Array.isArray(incomingValue)) {
    return incomingValue.map((item) => deepClone(item))
  }

  if (isPlainObject(incomingValue)) {
    const base = isPlainObject(targetValue) ? targetValue : {}
    deepMergeInPlace(base, incomingValue)
    return base
  }

  return incomingValue
}

function deepClone<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => deepClone(item)) as unknown as T
  }

  if (isPlainObject(value)) {
    const clone: Record<string, unknown> = {}
    Object.entries(value).forEach(([key, entry]) => {
      clone[key] = deepClone(entry)
    })
    return clone as T
  }

  return value
}

function isPlainObject(value: unknown): value is Record<string, any> {
  return Object.prototype.toString.call(value) === '[object Object]'
}

